import { useEffect, useState } from 'react';

export interface TypewriterProps {
  readonly text: string;
  readonly className?: string;
  /** ms per character while typing */
  readonly typeSpeed?: number;
  /** ms per character while deleting */
  readonly deleteSpeed?: number;
  /** ms to hold the full string before it deletes */
  readonly holdDelay?: number;
  /** ms to wait after clearing before typing again */
  readonly restartDelay?: number;
}

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Continuously loops a typewriter effect over `text` (type → hold → delete →
 * restart). Honours `prefers-reduced-motion` by rendering the full string with
 * no timers. The visible glyphs and caret are `aria-hidden`; the element keeps
 * a stable accessible name via `aria-label`, so screen readers announce the
 * text once rather than on every keystroke. Works with RTL text — it only
 * slices the string, and layout direction comes from the surrounding `dir`.
 */
export function Typewriter({
  text,
  className,
  typeSpeed = 55,
  deleteSpeed = 28,
  holdDelay = 2400,
  restartDelay = 700,
}: TypewriterProps): React.JSX.Element {
  const [count, setCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // Reset whenever the source text changes (e.g. a language switch).
  useEffect(() => {
    if (reducedMotion()) {
      setCount(text.length);
      setDeleting(false);
      return;
    }
    setCount(0);
    setDeleting(false);
  }, [text]);

  useEffect(() => {
    if (reducedMotion()) return;
    let id: number;
    if (!deleting) {
      id =
        count < text.length
          ? window.setTimeout(() => setCount((c) => c + 1), typeSpeed)
          : window.setTimeout(() => setDeleting(true), holdDelay);
    } else {
      id =
        count > 0
          ? window.setTimeout(() => setCount((c) => c - 1), deleteSpeed)
          : window.setTimeout(() => setDeleting(false), restartDelay);
    }
    return () => window.clearTimeout(id);
  }, [count, deleting, text, typeSpeed, deleteSpeed, holdDelay, restartDelay]);

  return (
    <span className={className} aria-label={text}>
      <span aria-hidden="true">{text.slice(0, count)}</span>
      <span aria-hidden="true" className="dd-caret" />
    </span>
  );
}
